<template>
  <div class="cfc-data-view">
    <div class="section-header">
      <h3>CFC数据查询</h3>
      <div class="filter-controls">
        <el-select 
          v-model="selectedProject" 
          placeholder="选择CFC项目" 
          clearable
          @change="handleProjectChange"
          style="width: 200px; margin-right: 10px;"
        >
          <el-option
            v-for="project in projects"
            :key="project.project_id"
            :label="project.project_name"
            :value="project.project_id">
          </el-option>
        </el-select>
        <el-button type="primary" @click="loadData" :loading="loading">
          <i class="el-icon-search"></i> 查询
        </el-button>
      </div>
    </div>

    <div class="data-table-container">
      <el-table 
        :data="tableData" 
        v-loading="loading"
        stripe
        border
        style="width: 100%"
        :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
      >
        <el-table-column prop="project_name" label="项目名称" width="180">
          <template slot-scope="scope">
            <el-tag type="info" size="small">{{ scope.row.project_name }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="course_name" label="课程名称" width="150"></el-table-column>
        <el-table-column prop="raw_score" label="原始分数" width="100" align="center">
          <template slot-scope="scope">
            <span class="score-value">{{ scope.row.raw_score }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="grade_level" label="等级" width="80" align="center">
          <template slot-scope="scope">
            <el-tag 
              :type="getGradeType(scope.row.raw_score, scope.row.pass_score, scope.row.excellent_score)"
              size="small"
            >
              {{ scope.row.grade_level || '无' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="标准分数" width="120" align="center">
          <template slot-scope="scope">
            <div class="standard-scores">
              <div class="score-item">
                <span class="score-label">及格:</span>
                <span class="score-value">{{ scope.row.pass_score }}</span>
              </div>
              <div class="score-item">
                <span class="score-label">优秀:</span>
                <span class="score-value">{{ scope.row.excellent_score }}</span>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="记录时间" width="160" align="center"></el-table-column>
        <el-table-column label="操作" width="100" align="center">
          <template slot-scope="scope">
            <el-button 
              type="text" 
              size="small" 
              @click="showDetail(scope.row)"
            >
              详情
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.page"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pagination.pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total">
        </el-pagination>
      </div>
    </div>

    <!-- 详情对话框 -->
    <el-dialog title="CFC成绩详情" :visible.sync="detailDialogVisible" width="600px">
      <div v-if="selectedRecord" class="detail-content">
        <div class="detail-item">
          <label>项目名称:</label>
          <span>{{ selectedRecord.project_name }}</span>
        </div>
        <div class="detail-item">
          <label>课程名称:</label>
          <span>{{ selectedRecord.course_name }}</span>
        </div>
        <div class="detail-item">
          <label>原始分数:</label>
          <span class="score-highlight">{{ selectedRecord.raw_score }}</span>
        </div>
        <div class="detail-item">
          <label>等级:</label>
          <el-tag 
            :type="getGradeType(selectedRecord.raw_score, selectedRecord.pass_score, selectedRecord.excellent_score)"
            size="medium"
          >
            {{ selectedRecord.grade_level || '无等级' }}
          </el-tag>
        </div>
        <div class="detail-item">
          <label>及格分数:</label>
          <span>{{ selectedRecord.pass_score }}</span>
        </div>
        <div class="detail-item">
          <label>优秀分数:</label>
          <span>{{ selectedRecord.excellent_score }}</span>
        </div>
        <div class="detail-item">
          <label>记录时间:</label>
          <span>{{ selectedRecord.created_at }}</span>
        </div>
        <div class="detail-item" v-if="selectedRecord.project_description">
          <label>项目描述:</label>
          <p class="description-text">{{ selectedRecord.project_description }}</p>
        </div>
        <div class="detail-item" v-if="selectedRecord.course_description">
          <label>课程描述:</label>
          <p class="description-text">{{ selectedRecord.course_description }}</p>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getCfcData, getCfcProjects } from '../../../api/dataQueryApi'

export default {
  name: 'CfcDataView',
  data() {
    return {
      loading: false,
      tableData: [],
      projects: [],
      selectedProject: null,
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      detailDialogVisible: false,
      selectedRecord: null
    }
  },
  mounted() {
    this.loadProjects()
    this.loadData()
  },
  methods: {
    async loadProjects() {
      try {
        const response = await getCfcProjects()
        this.projects = response.data
      } catch (error) {
        console.error('获取CFC项目失败:', error)
      }
    },
    async loadData() {
      try {
        this.loading = true
        const params = {
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        }
        
        if (this.selectedProject) {
          params.project_id = this.selectedProject
        }
        
        const response = await getCfcData(params)
        this.tableData = response.data.results
        this.pagination.total = response.data.total
      } catch (error) {
        console.error('获取CFC数据失败:', error)
        this.$message.error(error.message || '获取CFC数据失败')
      } finally {
        this.loading = false
      }
    },
    handleProjectChange() {
      this.pagination.page = 1
      this.loadData()
    },
    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.pagination.page = 1
      this.loadData()
    },
    handleCurrentChange(val) {
      this.pagination.page = val
      this.loadData()
    },
    showDetail(row) {
      this.selectedRecord = row
      this.detailDialogVisible = true
    },
    getGradeType(score, passScore, excellentScore) {
      if (score >= excellentScore) {
        return 'success'
      } else if (score >= passScore) {
        return 'warning'
      } else {
        return 'danger'
      }
    }
  }
}
</script>

<style scoped>
.cfc-data-view {
  padding: 0;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #ebeef5;
}

.section-header h3 {
  color: #333;
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}

.filter-controls {
  display: flex;
  align-items: center;
}

.data-table-container {
  background: white;
}

.score-value {
  font-weight: 600;
  color: #409eff;
}

.standard-scores {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.score-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
}

.score-label {
  color: #909399;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}

.detail-content {
  padding: 10px 0;
}

.detail-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 15px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.detail-item:last-child {
  border-bottom: none;
}

.detail-item label {
  width: 100px;
  color: #666;
  font-weight: 500;
  flex-shrink: 0;
}

.detail-item span {
  color: #333;
  flex: 1;
}

.score-highlight {
  font-size: 18px;
  font-weight: 600;
  color: #409eff;
}

.description-text {
  margin: 0;
  line-height: 1.6;
  color: #666;
}

@media (max-width: 768px) {
  .section-header {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }
  
  .filter-controls {
    justify-content: center;
  }
  
  .detail-item {
    flex-direction: column;
    gap: 5px;
  }
  
  .detail-item label {
    width: auto;
  }
}
</style>
